import { Trans } from "@lingui/macro";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import { Alert, type AlertProps, AlertTitle, type SxProps, Typography } from "@mui/material";

type Props = {
  kind: "info" | "warning" | "tip" | "success" | "important-info" | "important-warning";
};

const shared: SxProps = {
  color: "hsla(213, 92%, 17%, 1)",
  borderRadius: "16px",
  paddingY: "12px",
  paddingX: "24px",
};

export function HCAlert(props: AlertProps & Props): JSX.Element {
  const { kind, children, ...restProps } = props;
  switch (kind) {
    case "success":
      return (
        <Alert
          variant="outlined"
          severity="success"
          {...restProps}
          sx={{
            border: "1px solid hsla(137, 81%, 23%, 1)",
            backgroundColor: "var(--color-signal-success-300)",
            ...shared,
          }}
          icon={<CheckCircleOutlineOutlinedIcon />}
        >
          <Typography variant="body1">{children}</Typography>
        </Alert>
      );
    case "info":
      return (
        <Alert
          variant="outlined"
          severity="info"
          {...restProps}
          sx={{
            border: "1px solid var(--color-brand-secondary-500)",
            backgroundColor: "transparent",
            ...shared,
          }}
        >
          <Typography variant="body1">{children}</Typography>
        </Alert>
      );
    case "warning":
      return (
        <Alert
          icon={<InfoOutlinedIcon sx={{ color: "var(--color-signal-danger-500)" }} />}
          variant="outlined"
          severity="warning"
          {...restProps}
          sx={{
            border: "1px solid var(--color-signal-danger-500)",
            ...shared,
            color: "var(--color-signal-danger-500)",
            backgroundColor: "var(--color-signal-danger-300)",
          }}
        >
          <Typography variant="body1">{children}</Typography>
        </Alert>
      );
    case "tip":
      return (
        <Alert
          icon={<VolumeUpOutlinedIcon sx={{ color: "var(--color-brand-secondary-400)" }} />}
          severity="warning"
          sx={{ backgroundColor: "transparent" }}
          {...restProps}
        >
          <Typography variant="body1">{children}</Typography>
        </Alert>
      );
    case "important-info":
      return (
        <Alert
          severity="info"
          sx={{
            backgroundColor: "transparent",
            border: "1px solid var(--color-brand-secondary-500)",
            ...shared,
          }}
          {...restProps}
        >
          <AlertTitle>
            <Typography variant="h2">
              <Trans id="fullscreen.title">Important</Trans>
            </Typography>
          </AlertTitle>
          <Typography variant="body1">{children}</Typography>
        </Alert>
      );

    case "important-warning":
      return (
        <Alert
          severity="warning"
          icon={<InfoOutlinedIcon sx={{ color: "var(--color-signal-danger-500)" }} />}
          sx={{
            backgroundColor: "transparent",
            border: "1px solid var(--color-signal-danger-500)",
            ...shared,
          }}
        >
          <AlertTitle>
            <Typography variant="h2">
              <Trans id="fullscreen.title">Important</Trans>
            </Typography>
          </AlertTitle>
          {children}
        </Alert>
      );
  }
}
